/**
* @Author: Gang Jiang
* @Date: 2026-03-05 14:39:04
* @Last Modified by: Gang Jiang
* @Last Modified Time: 2026-03-18 14:35:14
* @Description: Database table: influencer_marketing.rpt_revenue_flow
*/

<template>
  <div class="payments-commission-detail" ref="zjdgx">
    <div class="bread-crumb">
      Payments<span @click="router.push('/payments/dm-received')">DM Received</span><span class="current">Commission
        Details</span>
    </div>
    <h2>Payment</h2>

    <div class="summary-filter">
      <div class="summary-row">
        <div class="summary-item">
          Payment Date:
          <span class="value">{{ headerInfo.affiliatePaymentDate || '-' }}</span>
        </div>
        <div class="summary-item">
          Payment Status:
          <span class="value">{{ headerInfo.status || '-' }}</span>
        </div>
        <div class="summary-item">
          Affiliate:
          <span class="value">{{ headerInfo.affiliate || '-' }}</span>
        </div>
      </div>

      <a-form :model="filter" class="filter-form">
        <a-row :gutter="[16, 16]" align="middle">
          <a-col :xs="24" :sm="12" :md="8">
            <a-form-item class="filter-item" label="Influencer Username">
              <a-auto-complete v-model:value="influencerUsernameInput" :allowClear="true" placeholder="Please enter"
                :options="userNameSuggestions" @select="onUserSelect" @keydown.enter.stop="changeUser"
                @clear="clearUser" @search="onUserSearch" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8">
            <a-form-item class="filter-item" label="Advertiser Name">
              <a-select v-model:value="advertiserState.value" showSearch allowClear class="advertiser-select"
                placeholder="Please enter" :filter-option="false"
                :not-found-content="advertiserState.fetching ? undefined : null" :options="advertiserState.data"
                @blur="advertiserBlur" @clear="clearAdvertiser" @select="onAdvertiserSelect"
                @search="onAdvertiserSearch">
                <template v-if="advertiserState.fetching" #notFoundContent>
                  <a-spin size="small" />
                </template>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="24" :md="8" class="btns">
            <a-button type="primary" @click="handleSearch">Search</a-button>
            <a-button @click="handleReset">Reset</a-button>
            <a :href="exportUrl" download>
              <a-button type="primary">Export</a-button>
            </a>
          </a-col>
        </a-row>
      </a-form>

      <div class="summary-stats">
        <span>Payment Amount Original: {{headerInfo.currencySymbol}}{{ headerInfo.affiliatePaymentAmount }} | Original Commission All: {{headerInfo.currencySymbol}}{{
          headerInfo.totalOriginalCommission }}</span>
        <span>Finance Confirm Received Amount USD: ${{ headerInfo.financeConfirmReceivedAmountUsd }} | Commission USD
          All: ${{
            headerInfo.totalCommissionUsd }}</span>
      </div>
    </div>

    <div class="commission-table">
      <a-table :data-source="commissionList" :columns="columns" :loading="isLoading" :scroll="{ x: 2600 }" size="small"
        :pagination="pagination" :showSorterTooltip="false" :row-class-name="commissionRowClassName">
        <template #headerCell="{ column }">
          <span>{{ column.key }}</span>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="
            column.dataIndex === 'eventTime' ||
            column.dataIndex === 'updateTime'
          ">
            {{
              record[column.dataIndex]
                ? moment(record[column.dataIndex]).format('YYYY-MM-DD HH:mm:ss')
                : '-'
            }}
          </template>
          <template v-else-if="column.dataIndex === 'commissionUsd' || column.dataIndex === 'originalCommission'">
            <span :class="record['commissionNotEqual'] ? 'red-mark' : ''">{{ record[column.dataIndex] }}</span>
          </template>
          <template v-else-if="column.key === 'Status'">
            <span :class="record.status?.replace(' ', '-').toLowerCase() + (record.redMark ? ' red-mark' : '')">{{
              record.status
            }}</span>
          </template>
          <template v-else-if="column.key === 'Actions'">
            <a href="javascript:;" @click="setShowLogModalId(record.commissionId)">Log</a>
          </template>
          <template v-else>
            {{ record[column.dataIndex] }}
          </template>
        </template>
      </a-table>
    </div>

    <a-modal title="Log" class="log-modal" :width="1000" :footer="null" v-model:open="showLogModal"
      @cancel="hideLogModal" wrapClassName="log-modal-wrapper" :getContainer="$refs.zjdgx">
      <a-spin :spinning="logLoading">
        <a-table :data-source="logList" :columns="LogTableColumns" bordered :pagination="logPagination"
          :scroll="{ y: 480 }">
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'createTime'">
              {{ moment(record.createTime).format('YYYY-MM-DD HH:mm:ss') }}
            </template>
          </template>
        </a-table>
      </a-spin>
    </a-modal>
  </div>
</template>

<script setup>
import { message } from 'ant-design-vue'
import { ref, reactive, computed, watch, onMounted, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import { debounce } from 'lodash-es'
import moment from 'moment'
import { apiDomain } from '../../../config'
import payments from '../../server/payments'
import advertiser from '../../server/advertiser'
import { user, commissions } from '../../server/index'
import { useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const headerInfo = ref({})
const paymentId = route.params.id

// 当前查看日志的 commissionId
const currentCommissionId = ref(null)

const filter = ref({
  influencerId: '',
  advertiserId: '',
})

// 展示在输入框里的 Influencer Username 文本
const influencerUsernameInput = ref('')

// Influencer username 自动补全候选
const userNameSuggestions = ref([])

const commissionList = ref([])
const isLoading = ref(false)

const commissionRowClassName = (record) =>
  record?.commissionNotEqual ? 'commission-not-equal-row' : ''
const pagination = ref({
  current: 1,
  pageSize: 200,
  total: 0,
  showSizeChanger: false,
  onChange: (page, pageSize) => {
    pagination.value.current = page
    pagination.value.pageSize = pageSize
    fetchList()
  },
})

const exportUrl = computed(() => {
  const params = []

  if (filter.value.influencerId) {
    params.push(`influencerId=${filter.value.influencerId}`)
  }
  if (filter.value.advertiserId) {
    params.push(`advertiserId=${filter.value.advertiserId}`)
  }

  const query = params.length ? `?${params.join('&')}` : ''
  const base = `https://${apiDomain}/admin/influencer-marketing/v1/finance/dm-received/payment/${paymentId}/details/export`

  return `${base}${query}`
})

// log 相关
const logLoading = ref(false)
const showLogModal = ref(false)
const logList = ref([])
const LogTableColumns = [
  { title: 'Editor', dataIndex: 'editorName', key: 'editorName' },
  { title: 'Operation', dataIndex: 'operation', key: 'operation' },
  { title: 'Date', dataIndex: 'createTime', key: 'createTime' },
]
const logPagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: false,
  onChange: (page) => {
    getLog(page)
  },
})

const getLog = (page) => {
  if (!currentCommissionId.value) return

  logLoading.value = true
  commissions
    .getLogList({
      id: currentCommissionId.value,
      page,
      size: 10,
    })
    .then((res) => {
      logLoading.value = false

      if (res.success) {
        logList.value = res.data

        if (page === 1) {
          logPagination.value.total = res.total
        }

        logPagination.value.current = page
      } else {
        message.error(res.error)
        logLoading.value = false
      }
    })
}

// advertiser name 搜索
let lastFetchId = 0
const advertiserState = reactive({
  data: [],
  value: null,
  fetching: false,
})

const onAdvertiserSelect = (id) => {
  // 直接使用选中的 advertiserId
  filter.value.advertiserId = id || ''
  handleSearch()
}

const advertiserBlur = () => {
  advertiserState.data = []
}

const clearAdvertiser = () => {
  advertiserState.value = null
  filter.value.advertiserId = ''
  handleSearch()
}

const onAdvertiserSearch = debounce((value) => {
  const keyword = value.trim()
  if (!keyword.length) {
    advertiserState.data = []
    advertiserState.fetching = false
    return
  }

  lastFetchId += 1
  const fetchId = lastFetchId
  advertiserState.data = []
  advertiserState.fetching = true

  advertiser
    .getSuggestionList({
      name: keyword,
      size: 20,
    })
    .then((res) => {
      if (fetchId !== lastFetchId) {
        return
      }

      if (res.success) {
        advertiserState.data = res.data.map((item) => ({
          label: item.advertiserName,
          value: item.id,
        }))
      }

      advertiserState.fetching = false
    })
}, 300)

watch(
  () => advertiserState.value,
  (val) => {
    if (!val) {
      advertiserState.data = []
      advertiserState.fetching = false
    }
  }
)

const columns = [
  {
    dataIndex: 'paymentId',
    key: 'Payment ID',
    width: 110,
  },
  {
    dataIndex: 'affiliatePaymentId',
    key: 'Affiliate Payment ID',
    width: 150,
  },
  {
    dataIndex: 'status',
    key: 'Status',
    width: 90,
  },
  {
    dataIndex: 'type',
    key: 'Type',
    width: 70,
  },
  {
    dataIndex: 'commissionId',
    key: 'Commission ID',
    width: 120,
  },
  {
    dataIndex: 'eventTime',
    key: 'Event Time',
    width: 180,
  },
  {
    dataIndex: 'updateTime',
    key: 'Update Time',
    width: 180,
  },
  {
    dataIndex: 'commissionUsd',
    key: 'Commission USD',
    width: 150,
  },
  {
    dataIndex: 'commissionForKolUsd',
    key: 'Commission for KOL USD',
    width: 190,
  },
  {
    dataIndex: 'commissionForKolRatio',
    key: 'Commission for KOL Ratio',
    width: 200,
  },
  {
    dataIndex: 'commissionCurrency',
    key: 'Commission Currency',
    width: 180,
  },
  {
    dataIndex: 'originalCommission',
    key: 'Original Commission',
    width: 170,
  },
  {
    dataIndex: 'currencyRate',
    key: 'Currency Rate',
    width: 120,
  },
  {
    dataIndex: 'influencerUsername',
    key: 'Influencer Username',
    width: 160,
  },
  {
    dataIndex: 'influencerEmail',
    key: 'Influencer Email',
    width: 200,
  },
  {
    dataIndex: 'affiliate',
    key: 'Affiliate',
    width: 180,
  },
  {
    dataIndex: 'advertiser',
    key: 'Advertiser',
    width: 180,
  },
  {
    dataIndex: 'affiliateMid',
    key: 'Affiliate MID',
    width: 120,
  },
  {
    dataIndex: 'orderId',
    key: 'Order ID',
    width: 160,
  },
  {
    dataIndex: 'affiliateCommissionId',
    key: 'A.Commission ID',
    width: 140,
  },
  {
    dataIndex: 'itemId',
    key: 'Item ID(SKU)',
    width: 120,
  },
  {
    dataIndex: 'note',
    key: 'Note',
    width: 200,
  },
  {
    key: 'Actions',
    dataIndex: 'actions',
    width: 80,
    fixed: 'right',
  },
]

const fetchList = () => {
  isLoading.value = true

  payments.getCommissionList({
    paymentId: paymentId,
    type: 'dmReceived',
    ...filter.value,
    page: pagination.value.current,
    size: pagination.value.pageSize,
  }).then((res) => {
    if (res.success) {
      commissionList.value = res.data
      pagination.value.total = res.total
      isLoading.value = false
    } else {
      message.error(res.message)
      isLoading.value = false
    }
  })
}

const handleSearch = () => {
  pagination.value.current = 1
  fetchList()
}

const handleReset = () => {
  filter.value.influencerId = ''
  filter.value.advertiserId = ''
  influencerUsernameInput.value = ''
  advertiserState.value = null
  advertiserState.data = []
  advertiserState.fetching = false
  userNameSuggestions.value = []
  handleSearch()
}

const onUserSearch = (value) => {
  const keyword = String(value || '').trim()
  if (!keyword) {
    userNameSuggestions.value = []
    return
  }

  user
    .getSuggestionList('userName', {
      userName: keyword,
      size: 20,
    })
    .then((res) => {
      if (res.success) {
        userNameSuggestions.value = res.data.map((item) => ({
          label: item.userName,
          value: item.id,
        }))
      }
    })
}

const onUserSelect = (id, option) => {
  // 保存选中的 influencerId，输入框展示 username
  filter.value.influencerId = id || ''
  influencerUsernameInput.value = option?.label || ''
  handleSearch()
}

const changeUser = (e) => {
  // 只更新显示文案，不修改已选中的 influencerId
  influencerUsernameInput.value = String(e?.target?.value || '').trim()
}

const clearUser = () => {
  influencerUsernameInput.value = ''
  filter.value.influencerId = ''
  userNameSuggestions.value = []
  handleSearch()
}

const setShowLogModalId = (commissionId) => {
  currentCommissionId.value = commissionId
  showLogModal.value = true
  getLog(1)
}

const hideLogModal = () => {
  currentCommissionId.value = null
  showLogModal.value = false
}

onBeforeMount(() => {
  payments.getDmReceivedDetailStats(paymentId).then((res) => {

    if (res.affiliate) {
      headerInfo.value = { ...res, affiliatePaymentDate: res.affiliatePaymentDate ? moment(res.affiliatePaymentDate).format('YYYY-MM-DD') : '' }
    } else {
      message.error(res.message)
    }
  })
})

onMounted(() => {
  fetchList()
})
</script>

<style lang="less" scoped>
.payments-commission-detail {
  .bread-crumb {
    margin-bottom: 4px;

    >span {
      cursor: pointer;
      margin-left: 25px;
      position: relative;

      &:before {
        top: -1px;
        left: -14px;
        content: '/';
        font-size: 14px;
        color: #999;
        position: absolute;
      }
    }
  }

  .summary-filter {
    margin: 12px 0;
    padding: 16px 20px;
    background: #fff;
    border-radius: 4px;

    .summary-row {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
      margin-bottom: 12px;
      font-size: 14px;

      .summary-item {
        color: #333;

        .value {
          font-weight: 600;
          margin-left: 4px;
        }
      }
    }

    .filter-form {
      .filter-item {
        margin-bottom: 0;
      }

      .btns {
        width: 100%;
        text-align: right;
        white-space: nowrap;

        .ant-btn {
          margin-left: 12px;
        }
      }

      @media (max-width: 768px) {
        .btns {
          text-align: left;
          white-space: normal;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;

          .ant-btn {
            margin-left: 0;
          }
        }
      }
    }

    .summary-stats {
      gap: 40px;
      display: flex;
      font-size: 14px;
      margin-top: 18px;
      align-items: center;
    }
  }

  :deep(.commissions) {

    .bread-crumb,
    h2,
    .filter,
    .statistic {
      display: none;
    }

    .commission-table {
      margin-top: 0;
    }
  }
}

.red-mark {
  color: #ff4d4f;
}

.commission-table :deep(tr.commission-not-equal-row > td) {
  background: #FCE5E7;
}
</style>
